import React from 'react';
import { Target, AlertTriangle, CheckCircle2 } from 'lucide-react';

export interface PerformanceMetric {
  label: string;
  value: number;
  target?: number;
  unit?: string;
  color?: string;
  description?: string;
}

export interface PerformanceMetricsProps {
  metrics: PerformanceMetric[];
  title?: string;
  className?: string;
}

const CircularProgress: React.FC<{
  value: number;
  size?: number;
  strokeWidth?: number;
  color: string;
  max?: number;
}> = ({ value, size = 100, strokeWidth = 10, color, max = 100 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percentage = Math.min((value / max) * 100, 100);
  const offset = circumference - (percentage / 100) * circumference;

  const getColorClass = () => {
    if (color) return color;
    if (percentage >= 80) return 'text-green-500';
    if (percentage >= 60) return 'text-blue-500';
    if (percentage >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-200 dark:text-gray-700"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={getColorClass()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {Math.round(value)}
        </span>
        {max !== 100 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            / {max}
          </span>
        )}
      </div>
    </div>
  );
};

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  metrics,
  title = 'Performance Metrics',
  className = '',
}) => {
  if (!metrics || metrics.length === 0) {
    return (
      <div className={`card p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No performance data available
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
        <Target className="h-5 w-5 mr-2 text-blue-600" />
        {title}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {metrics.map((metric, index) => {
          const percentage = metric.target ? (metric.value / metric.target) * 100 : metric.value;
          const isAboveTarget = metric.target ? metric.value >= metric.target : false;
          const isWarning = metric.target ? percentage < 60 : metric.value < 40;
          
          return (
            <div
              key={index}
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <CircularProgress
                value={metric.value}
                max={metric.target || 100}
                color={metric.color}
                size={100}
                strokeWidth={8}
              />
              
              <div className="mt-4 text-center w-full">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {metric.label}
                </p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {metric.value.toFixed(1)}
                  </span>
                  {metric.unit && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {metric.unit}
                    </span>
                  )}
                </div>
                
                {metric.target && (
                  <div className="flex items-center justify-center gap-1 text-xs">
                    {isAboveTarget ? (
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                    ) : isWarning ? (
                      <AlertTriangle className="h-3 w-3 text-yellow-500" />
                    ) : (
                      <Target className="h-3 w-3 text-gray-400" />
                    )}
                    <span className="text-gray-500 dark:text-gray-400">
                      Target: {metric.target}
                    </span>
                  </div>
                )}
                
                {metric.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    {metric.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
